import React from 'react';
import styled from 'styled-components';

import OnClickOutside from '../../../OnClickOutside';
import Avatar from '../../Clients/Avatar';
import CommunicationFilters from '../CommunicationFilters';
import Button from '../../../Button';
import LogList from './components/LogList';
import Name from '../../Clients/Name';
import LogRelations from '../LogRelations';
import NoteForm from '../../Notes/NoteForm';

import {
  LOG_RELATIONS,
  TICKET_LOG_TYPES,
  CLIENT_LOG_TYPES,
} from '../../../../constants';

import ITicketLog from './interfaces/ITicketLog';
import IClientLog from './interfaces/IClientLog';
import { AttachmentType } from '@components/CRM/Chains/Message2/Message2';
import { ValueOf } from '@utils/tsUtils';

interface Contact {
  avatarUrl: string;
  fullName: string;
}

export interface Props {
  className?: string;
  logs: IClientLog[] | ITicketLog[];
  contact: Contact;
  hasNextPage: boolean;
  logsMeta: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
  threshold: number;
  minimumBatchSize: number;
  isNextPageLoading: boolean;
  selectedCallId: string | null;
  types: unknown;
  relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  filteredType:
    | (typeof CLIENT_LOG_TYPES)[keyof typeof CLIENT_LOG_TYPES][]
    | (typeof TICKET_LOG_TYPES)[keyof typeof TICKET_LOG_TYPES][];
  onClickIcon: (params: {
    id: string;
    type:
      | (typeof CLIENT_LOG_TYPES)[keyof typeof CLIENT_LOG_TYPES]
      | (typeof TICKET_LOG_TYPES)[keyof typeof TICKET_LOG_TYPES];
    isBot: boolean;
    ticketId: string;
  }) => void;
  onFilter(params: unknown): void;
  onRelationChange(params: unknown): void;
  onDelete(params: { id: string }): void;
  onEdit(params: { id: string }): void;
  onAdd(params: {
    value: string;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
    attachments: AttachmentType[];
    onSuccess?: () => void;
    onError?: () => void;
  }): void;
  onLoadRequest(page: number): void;
  onPin(params: {
    id: string;
    isPinned: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onFlag(params: {
    id: string;
    isFlagged: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onReasonDelete(params: { callId: string; [propName: string]: unknown }): void;
  onReasonSubmit(params: { callId: string; [propName: string]: unknown }): void;
  toggleTicketLogsGroup(params: { id: string }): void;
  onDeleteAttachment(params: {
    attachId: string;
    noteId: string;
    relateTo: ValueOf<typeof LOG_RELATIONS>;
  }): void;
}

interface State {
  isNoteFormOpen: boolean;
}

const defaultProps: Partial<Props> = {
  hasNextPage: false,
  isNextPageLoading: false,
};

class Logs extends React.PureComponent<Props, State> {
  static defaultProps = defaultProps as Partial<Props>;

  constructor(props: Props) {
    super(props);

    this.state = {
      isNoteFormOpen: false,
    };

    this.submitNote = this.submitNote.bind(this);
    this.toggleForm = this.toggleForm.bind(this);
    this.closeForm = this.closeForm.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
  }

  handleTypeChange({ detail }) {
    const { onRelationChange } = this.props;
    const relateTo = detail.value[0];
    onRelationChange(relateTo);
  }

  submitNote({ value, attachments, onSuccess, onError }) {
    const { relateTo, onAdd } = this.props;
    onAdd({
      value,
      attachments,
      relateTo,
      onSuccess: () => {
        onSuccess?.();
        this.closeForm();
      },
      onError,
    });
  }

  toggleForm() {
    this.setState(prevState => ({
      isNoteFormOpen: !prevState.isNoteFormOpen,
    }));
  }

  closeForm() {
    this.setState({ isNoteFormOpen: false });
  }

  render() {
    const {
      contact,
      hasNextPage,
      isNextPageLoading,
      selectedCallId,
      logs,
      logsMeta,
      onClickIcon,
      onDelete,
      relateTo,
      filteredType,
      onFilter,
      onLoadRequest,
      onPin,
      onFlag,
      onEdit,
      onReasonDelete,
      onReasonSubmit,
      toggleTicketLogsGroup,
      onDeleteAttachment,
      threshold,
      types,
      ...props
    } = this.props;
    const { avatarUrl, fullName } = contact;
    const { isNoteFormOpen } = this.state;

    return (
      <StyledLogs {...props}>
        <StyledHead>
          <StyledContact>
            <StyledAvatar name={fullName} url={avatarUrl} />
            {fullName && <StyledName name={fullName} />}
          </StyledContact>
        </StyledHead>
        {isNoteFormOpen ? (
          <StyledNoteFormContainer
            onClickOutside={this.closeForm}
            ignoreSelectors={['.action-popup']}
          >
            <StyledNoteForm
              onSubmit={this.submitNote}
              onCancel={this.closeForm}
            />
          </StyledNoteFormContainer>
        ) : (
          <StyledNoteFormPlaceholder functional onClick={this.toggleForm}>
            Введите текст заметки
          </StyledNoteFormPlaceholder>
        )}
        <StyledLogRelations value={relateTo} onChange={this.handleTypeChange} />
        <StyledCommunicationFilters
          relateTo={relateTo}
          values={filteredType}
          types={types}
          onChange={onFilter}
        />
        {logs && (
          <StyledLogList
            logs={logs}
            logsMeta={logsMeta}
            relateTo={relateTo}
            hasNextPage={hasNextPage}
            isNextPageLoading={isNextPageLoading}
            selectedCallId={selectedCallId}
            onDelete={onDelete}
            onEdit={onEdit}
            onClickIcon={onClickIcon}
            onLoadRequest={onLoadRequest}
            onPin={onPin}
            onFlag={onFlag}
            onReasonDelete={onReasonDelete}
            onReasonSubmit={onReasonSubmit}
            threshold={threshold}
            toggleTicketLogsGroup={toggleTicketLogsGroup}
            onDeleteAttachment={onDeleteAttachment}
          />
        )}
      </StyledLogs>
    );
  }
}

const StyledLogs = styled.div`
  background-color: #e6e6e6;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 0 14px;
`;

const StyledHead = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-bottom: 10px;
`;

const StyledCommunicationFilters = styled(CommunicationFilters)<{
  [index: string]: unknown;
}>`
  margin-bottom: 5px;
`;

const StyledLogRelations = styled(LogRelations)<{
  [index: string]: unknown;
}>`
  margin-bottom: 10px;
`;

const StyledContact = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StyledAvatar = styled(Avatar)`
  height: 26px;
  width: 26px;
`;

const StyledName = styled(Name)`
  color: #17181a;

  ${StyledAvatar} + & {
    margin-left: 8px;
  }
`;

const StyledNoteFormContainer = styled(OnClickOutside)`
  height: 40px;
  margin-bottom: 15px;
  position: relative;
`;

// @ts-ignore: Skip jsx dependence check
const StyledNoteForm = styled(NoteForm)`
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  background-color: #fffbe5;
  box-shadow: 0 2px 20px 0 rgb(149 166 183 / 30%);
  position: absolute;
  z-index: 5;
`;

const StyledNoteFormPlaceholder = styled(Button)`
  background: white;
  border-radius: 4px;
  color: #c2c5cc;
  font-size: 14px;
  height: 40px;
  line-height: 40px;
  margin-bottom: 10px;
  padding: 0 8px;
  text-align: left;
`;

const StyledLogList = styled(LogList)`
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c2c5cc;
    border-radius: 40px;
  }
`;

export default Logs;
